<template>
  <div class="space-y-6">
    <div class="bg-white rounded-lg shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black mb-8">
      <div class="bg-blue-50 p-6 rounded-t-lg">
        <div class="flex items-center mb-1">
          <div class="w-8 h-8 rounded-lg flex items-center justify-start">
            <BookOpenIcon class="w-6 h-6 text-blue-800" />
          </div>
          <h2 class="text-2xl font-semibold text-blue-900">Session History</h2>
        </div>
        <p class="text-blue-800 font-semibold">View your completed and cancelled sessions.</p>
      </div>

      <div class="p-6">
        <!-- Search and Filter Section - Always visible -->
        <SessionSearchAndFilter
          v-model:searchQuery="searchQuery"
          v-model:statusFilter="statusFilter"
          v-model:subjectFilter="subjectFilter"
          filter-context="history"
        />

        <!-- No Sessions State - Only show when there are no sessions at all -->
        <div v-if="props.completedSessions.length === 0 && props.cancelledSessions.length === 0" class="bg-white rounded-lg pb-5 px-6 mt-6">
          <div class="text-center py-12">
            <CalendarIcon class="mx-auto h-12 w-12 text-gray-400" />
            <h3 class="mt-2 text-sm font-medium text-gray-900">No session history yet</h3>
            <p class="mt-1 text-sm text-gray-500">When you complete or cancel sessions, they'll appear here.</p>
          </div>
        </div>

        <!-- Completed Sessions Section -->
        <div v-if="props.completedSessions.length > 0">
          <!-- No filtered results for completed sessions -->
          <div v-if="filteredCompletedSessions.length === 0" class="bg-white rounded-lg pb-5 px-6 mt-6">
            <div class="flex items-center gap-3 mb-6">
              <div class="w-3 h-3 bg-green-500 rounded-full"></div>
              <h3 class="text-lg font-semibold text-gray-900">
                Completed Sessions (0)
              </h3>
            </div>
            <div class="text-center py-8">
              <CalendarIcon class="mx-auto h-12 w-12 text-gray-400" />
              <h3 class="mt-2 text-sm font-medium text-gray-900">No completed sessions match your search</h3>
              <p class="mt-1 text-sm text-gray-500">Try adjusting your search or filter criteria.</p>
            </div>
          </div>

          <!-- Use CompletedSessions component with pagination -->
          <div v-else>
            <CompletedSessions 
              :sessions="paginatedCompletedSessions"
              :total-sessions="filteredCompletedSessions.length"
            />
            
            <!-- Pagination for Completed Sessions -->
            <div v-if="totalPages > 1" class="bg-white rounded-lg mt-4 pb-3 sm:pb-5 px-3 sm:px-6">
              <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                <div class="text-xs sm:text-sm text-gray-600 order-2 sm:order-1">
                  Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ filteredCompletedSessions.length }} completed sessions
                </div>
                <div class="flex items-center space-x-1 overflow-x-auto w-full sm:w-auto order-1 sm:order-2">
                  <button
                    @click="previousPage"
                    :disabled="currentPage === 1"
                    class="border-2 border-blue-800 rounded-md px-2 sm:px-3 py-1 text-xs sm:text-sm text-blue-700 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1 flex-shrink-0"
                    title="Previous page"
                  >
                    <ChevronLeftIcon class="w-3 h-3 sm:w-4 sm:h-4" />
                    <span class="hidden sm:inline">Previous</span>
                    <span class="sm:hidden">Prev</span>
                  </button>
                  <button
                    v-for="page in totalPages"
                    :key="page"
                    @click="goToPage(page)"
                    :class="[
                      'px-2 sm:px-3 py-1 rounded-md border-2 border-blue-800 transition-colors min-w-[32px] sm:min-w-[40px] text-xs sm:text-sm flex-shrink-0',
                      page === currentPage
                        ? 'bg-blue-600 text-white font-semibold'
                        : 'text-blue-700 hover:bg-blue-50',
                    ]"
                  >
                    {{ page }}
                  </button>
                  <button
                    @click="nextPage"
                    :disabled="currentPage === totalPages"
                    class="border-2 border-blue-800 rounded-md px-2 sm:px-3 py-1 text-xs sm:text-sm text-blue-700 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1 flex-shrink-0"
                    title="Next page"
                  >
                    <span class="hidden sm:inline">Next</span>
                    <span class="sm:hidden">Next</span>
                    <ChevronRightIcon class="w-3 h-3 sm:w-4 sm:h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Cancelled Sessions Section -->
        <CancelledSessionsHistory
          :sessions="props.cancelledSessions"
          :total-sessions="props.totalCancelledSessions"
          user-type="student"
          :search-query="searchQuery"
          :subject-filter="subjectFilter"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import SessionSearchAndFilter from "@/components/Student-Tutor/SessionSearchAndFilter.vue";
import CancelledSessionsHistory from "@/components/Student-Tutor/CancelledSessionsHistory.vue";
import CompletedSessions from "@/components/Student-Tutor/CompletedSessions.vue";
import { BookOpenIcon, CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/24/outline";
import type { CompletedSession } from "@/types/session";

const router = useRouter();

interface LocalCompletedSession {
  id: string;
  tutorName: string;
  tutorProfileImage?: string | null;
  tutorId?: string;
  studentId?: string;
  status: "completed";
  type: "face-to-face" | "online";
  subject: string;
  date: string;
  time: string;
  location?: string;
  studentRating: string;
  tutorRating: string;
  feedback: string;
  feedbackStatus: "given" | "pending";
}

interface CancelledSession {
  id: string;
  tutorName?: string;
  tutorProfileImage?: string | null;
  type: "face-to-face" | "online";
  status: "cancelled" | "declined";
  subject: string;
  date: string;
  time: string;
  location?: string;
  cancellationReason?: string;
  cancelledBy?: "student" | "tutor" | "admin";
  cancelledAt: string;
  // Allow additional properties from the stores
  [key: string]: any;
}

interface Props {
  completedSessions?: LocalCompletedSession[];
  totalCompletedSessions?: number;
  cancelledSessions?: CancelledSession[];
  totalCancelledSessions?: number;
}

const props = withDefaults(defineProps<Props>(), {
  completedSessions: () => [],
  totalCompletedSessions: 0,
  cancelledSessions: () => [],
  totalCancelledSessions: 0,
});

const searchQuery = ref("");
const statusFilter = ref("all");
const subjectFilter = ref("all");

// Pagination state
const currentPage = ref(1);
const itemsPerPage = 4;

// Filter completed sessions based on search and filters
const filteredCompletedSessions = computed(() => {
  let filtered = props.completedSessions;

  // Apply search filter
  if (searchQuery.value) {
    filtered = filtered.filter(
      (session) =>
        (session.tutorName?.toLowerCase().includes(searchQuery.value.toLowerCase()) ?? false) ||
        session.subject.toLowerCase().includes(searchQuery.value.toLowerCase())
    );
  }

  // Apply subject filter
  if (subjectFilter.value !== "all") {
    filtered = filtered.filter((session) =>
      session.subject.toLowerCase().includes(subjectFilter.value.toLowerCase())
    );
  }

  return filtered;
});

// Get paginated sessions for current page with proper data mapping for CompletedSessions component
const paginatedCompletedSessions = computed((): CompletedSession[] => {
  return filteredCompletedSessions.value.slice(startIndex.value, endIndex.value).map(session => ({
    id: session.id,
    studentName: "Current Student", // This represents the logged-in student
    tutorName: session.tutorName,
    tutorProfileImage: session.tutorProfileImage,
    studentProfileImage: null, // Not available in student session history context
    tutorId: session.tutorId,
    studentId: session.studentId, 
    status: "completed" as const,
    type: session.type,
    mode: session.type, // Ensure mode property exists for CompletedSessions component
    date: session.date,
    time: session.time,
    subject: session.subject,
    location: session.location,
    yourRating: session.studentRating, // Map studentRating to yourRating for student view
    studentRating: session.studentRating,
    tutorRating: session.tutorRating,
    feedback: session.feedback,
    feedbackStatus: session.feedbackStatus
  }));
});

// Pagination calculations for completed sessions
const totalPages = computed(() => Math.ceil(filteredCompletedSessions.value.length / itemsPerPage));
const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage);
const endIndex = computed(() =>
  Math.min(startIndex.value + itemsPerPage, filteredCompletedSessions.value.length)
);

// Pagination methods for completed sessions
const goToPage = (page: number) => {
  currentPage.value = page;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};
</script>